// DEFAULT del acento configurable. La autoridad NO cambia: sigue siendo
// Administracion → Configuracion (`studio.accent_color`, solo rol
// ADMINISTRADOR); esto es el valor con el que arranca un estudio que todavia
// no configuro el suyo.
//
// Pasa de coral #eb6154 a rosa #f09e9e (2026-08-15, direccion visual humana):
// el rosa es el acento aprobado de Feria Design Studio y el que ya se ve en el
// portal cliente. El coral seguia siendo el default por herencia, y eso obligaba
// a las superficies aprobadas a esquivarlo con hexes fijos propios
// (`PORTAL_CTA`, `--cta`, `.pcta-cta:hover`), que es justo lo que se elimina.
// Cambia el VALOR, no el sistema.
export const DEFAULT_ACCENT_COLOR = '#f09e9e';

function clean(value = '') {
  return String(value || '').trim();
}

function rootStyle() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  try {
    return window.getComputedStyle(document.documentElement);
  } catch {
    return null;
  }
}

function readCssVariable(name) {
  const styles = rootStyle();
  if (!styles || !name) return '';
  return clean(styles.getPropertyValue(name));
}

function resolveCssColor(value, depth = 0) {
  const raw = clean(value);
  if (!raw || depth > 3) return '';
  const variableMatch = raw.match(/^var\((--[a-zA-Z0-9-_]+)(?:,\s*([^)]+))?\)$/);
  if (variableMatch) {
    return resolveCssColor(readCssVariable(variableMatch[1]) || variableMatch[2], depth + 1);
  }
  return raw;
}

function readStoredAccentColor() {
  if (typeof window === 'undefined') return '';
  try {
    return clean(window.localStorage.getItem('feria_accent_color'));
  } catch {
    return '';
  }
}

function expandHex(value) {
  const hex = clean(value).replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    return hex.split('').map(char => `${char}${char}`).join('');
  }
  if (/^[0-9a-f]{6}$/i.test(hex)) return hex;
  return '';
}

function colorToRgb(value) {
  const raw = resolveCssColor(value);
  const hex = expandHex(raw);
  if (hex) {
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }
  const rgbMatch = raw.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (rgbMatch) {
    return {
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3]),
    };
  }
  return null;
}

function colorWithAlpha(color, alpha) {
  const rgb = colorToRgb(color);
  if (rgb) return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
  return `color-mix(in srgb, ${color} ${Math.round(alpha * 100)}%, transparent)`;
}

function readableTextColor(color) {
  const rgb = colorToRgb(color);
  if (!rgb) return '#111111';
  const toLinear = value => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  };
  const luminance = (0.2126 * toLinear(rgb.r)) + (0.7152 * toLinear(rgb.g)) + (0.0722 * toLinear(rgb.b));
  // Fase B: se elige la tinta de MAYOR contraste (WCAG), no un umbral fijo.
  // El umbral 0.58 elegia blanco sobre coral #eb6154 (3.30:1, rompe AA);
  // la tinta oscura da 5.31:1.
  const DARK_INK_LUMINANCE = 0.0056; // #111111
  const contrastWithWhite = 1.05 / (luminance + 0.05);
  const contrastWithDark = (luminance + 0.05) / (DARK_INK_LUMINANCE + 0.05);
  return contrastWithDark >= contrastWithWhite ? '#111111' : '#FFFFFF';
}

export function getThemeAccentColor(fallback = DEFAULT_ACCENT_COLOR) {
  const candidates = [
    readCssVariable('--accent'),
    readCssVariable('--gold'),
    readStoredAccentColor(),
    fallback,
  ];

  for (const candidate of candidates) {
    const resolved = resolveCssColor(candidate);
    if (resolved) return resolved;
  }

  return fallback;
}

// Hover/enfasis: el acento se INTENSIFICA (mas saturado y algo mas oscuro),
// no se aclara ni se apaga. Es la mecanica D17 que ya usaba el portal —reposo
// rosa #f09e9e, enfasis coral #eb6154— pero expresada como derivacion, no como
// hex fijo: con el rosa aprobado cae practicamente en ese mismo coral, y con
// cualquier otro acento de tenant da su equivalente.
function intensificar(color) {
  const rgb = colorToRgb(color);
  if (!rgb) return color;
  const r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let sat = 0;
  if (d !== 0) {
    sat = d / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  // Calibrado contra el par aprobado del portal: rosa #f09e9e en reposo debe
  // caer en el coral #eb6154 al intensificar. Con estos dos factores da
  // #eb5454, indistinguible a simple vista del aprobado.
  const s2 = Math.min(1, sat * 1.08);
  const l2 = Math.max(0, l - 0.155);
  const c = (1 - Math.abs(2 * l2 - 1)) * s2;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l2 - c / 2;
  const [r1, g1, b1] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x]
    : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  const hex = (v) => Math.round(Math.min(255, Math.max(0, (v + m) * 255))).toString(16).padStart(2, '0');
  return `#${hex(r1)}${hex(g1)}${hex(b1)}`;
}

// Un indicador grafico necesita 3:1 contra su superficie. Intensificar una vez
// basta para casi todo, pero no para acentos muy luminosos: un amarillo puro
// se queda en 2,77:1. Se sigue intensificando hasta alcanzarlo, con tope, en
// vez de dar por bueno el primer intento.
const SUPERFICIE_CLARA = '#F5F5F4';
function contrasteContra(color, fondo) {
  const a = colorToRgb(color);
  const b = colorToRgb(fondo);
  if (!a || !b) return 0;
  const lin = (v) => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
  const lum = ({ r, g, b: bb }) => 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(bb);
  const x = lum(a); const y = lum(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}
function colorDeAtencion(accent) {
  let c = intensificar(accent);
  for (let i = 0; i < 6 && contrasteContra(c, SUPERFICIE_CLARA) < 3; i += 1) {
    c = intensificar(c);
  }
  return c;
}

export function getAccentColorTokens(inputColor) {
  const accent = resolveCssColor(inputColor) || getThemeAccentColor();
  return {
    accent,
    accentHover: intensificar(accent),
    accentFaint: colorWithAlpha(accent, 0.08),
    accentSoft: colorWithAlpha(accent, 0.12),
    accentBorder: colorWithAlpha(accent, 0.24),
    accentBorderStrong: colorWithAlpha(accent, 0.36),
    accentShadow: colorWithAlpha(accent, 0.18),
    accentText: accent,
    accentContrast: readableTextColor(accent),
    accentFocus: colorWithAlpha(accent, 0.45),
    accentInk: readableTextColor(accent),
    // ATENCION (sin leer, no error). Usa el acento INTENSIFICADO, no el acento
    // crudo: medido sobre la superficie clara, el rosa aprobado da 1,91:1 y el
    // indigo 2,99:1, por debajo del 3:1 que necesita un indicador grafico. El
    // intensificado pasa con cualquier acento de tenant probado.
    // Hoy coincide con accentHover porque la derivacion es la misma; se nombra
    // aparte porque el ROL es otro: si manana se afina el hover, "sin leer" no
    // debe moverse con el.
    accentAttention: colorDeAtencion(accent),
    accentAttentionInk: readableTextColor(colorDeAtencion(accent)),
  };
}
